import { useContext, useReducer } from 'react';
import { useMutation } from '@apollo/client';

import AuthContext from 'app/src/contexts/AuthContext';
import { EMPTY_PREFERENCES } from './preferences';
import { UPSERT_USER_PREFERENCE } from './queries';

// User preferences are grouped by type on the API so other kinds of UI state
// can share the store; column layouts are the 'table' ones.
const TABLE = 'table';

// Neither of the obvious homes for a saved preference survives a remount:
// AuthContext holds currentUser in React state from one query at login, and
// AuthProvider rebuilds the ApolloClient — cache included — on every render, so
// the Apollo cache is always empty. Saves made during this session live here
// instead; the database is the source of truth on the next full load.
const savedThisSession = new Map();

const sessionKey = (user, tableKey) => `${user?.id}:${tableKey}`;

// The store outlives any component by design, so tests have to empty it between
// cases the way a page load does.
export const resetSavedPreferences = () => savedThisSession.clear();

const parsePreferences = stored => {
  if (!stored) { return EMPTY_PREFERENCES; }

  try {
    return { ...EMPTY_PREFERENCES, ...JSON.parse(stored) };
  } catch (error) {
    return EMPTY_PREFERENCES;
  }
};

const storedFor = (user, tableKey) => (
  savedThisSession.get(sessionKey(user, tableKey)) ||
  (user?.userPreferences || []).find(preference => (
    TABLE === preference.preferenceType && preference.preferenceKey === tableKey
  ))?.preferences
);

const useTablePreferences = tableKey => {
  const { user } = useContext(AuthContext);
  const [, redraw] = useReducer(count => count + 1, 0);
  const [upsert] = useMutation(UPSERT_USER_PREFERENCE);

  // Derived rather than held in state: the tables mount before currentUser has
  // loaded, so a useState initializer would capture the empty list and keep it
  // even once the real preferences arrive.
  const preferences = parsePreferences(storedFor(user, tableKey));

  const save = next => {
    const payload = JSON.stringify(next);

    savedThisSession.set(sessionKey(user, tableKey), payload);
    redraw();

    upsert({
      variables: {
        preferenceType: TABLE,
        preferenceKey: tableKey,
        preferences: payload,
      },
    }).catch(() => {
      // A failed save must not break the table or discard the user's choice;
      // it still applies for this session and the next edit retries.
    });
  };

  return { preferences, save };
};

export default useTablePreferences;
